"use client";

import { useState, useRef, useEffect } from "react";
import { Search, Check, ChevronRight, InfoIcon } from "lucide-react";

const FilterableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = "Selecciona una opción",
  label,
  searchPlaceholder = "Buscar...",
  disabled = false,
  error,
  name,
  required = false,
  autoFocus = false,
  isDesktop = false,
  paddingRight = "clamp(100px, 4vh, 50px)",
  paddingLeft = "clamp(100px, 4vh, 50px)",
  colorText = "white",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const selectRef = useRef(null);
  const searchInputRef = useRef(null);
  const inputContainerRef = useRef(null);

  const selectedOption = options.find(
    (opt) => (opt.value || opt.label) === value,
  );

  useEffect(() => {
    setFilteredOptions(
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    );
  }, [searchTerm, options]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (autoFocus && !selectedOption) {
      const timeout = setTimeout(() => {
        setIsOpen(true);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [autoFocus, options, selectedOption]);

  // Calcular posición del dropdown
  const updateDropdownPosition = () => {
    if (inputContainerRef.current) {
      const rect = inputContainerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 10, // 10px de gap
        left: rect.left,
        width: rect.width,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen]);

  // Actualizar posición en scroll/resize
  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);

    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option.value || option.label } });
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div
      className="relative w-full"
      ref={selectRef}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        className="w-full border-none shadow-xs flex flex-col"
        style={{
          height: "100%",
          gap: "clamp(6px, 1vh, 10px)",
          paddingRight: isDesktop && paddingRight,
          paddingLeft: isDesktop && paddingLeft,
          justifyContent: "space-between"
        }}
      >
        {/* Label */}
        <span
          className="font-semibold2 flex"
          style={{
            fontSize: "clamp(14px, 1.4vw, 16px)",
            flexShrink: 0,
            color: colorText,
          }}
        >
          {label}
        </span>

        {/* Input principal */}
        <div
          ref={inputContainerRef}
          className="flex items-center transition-all duration-150"
          style={{
            backgroundColor: selectedOption
              ? "#565584"
              : isDesktop
                ? "#1C1535"
                : "#2C2447",
            borderRadius: "clamp(6px, 0.8vw, 8px)",
            border:
              error || selectedOption
                ? "2px solid #C9B8FF"
                : "2px solid transparent",
            padding: "clamp(8px, 1.2vh, 14px) clamp(10px, 1.4vw, 16px)",
            gap: "clamp(8px, 1.2vw, 12px)",
            minHeight: "clamp(48px, 6vh, 60px)",
            flexShrink: 0,
            opacity: disabled ? 0.5 : 1, // ← look deshabilitado
            cursor: disabled ? "not-allowed" : "default",
            pointerEvents: disabled ? "none" : "auto",
          }}
        >
          {selectedOption?.icon ? (
            <span
              style={{
                width: "clamp(25px, 2.5vw, 32px)",
                height: "clamp(25px, 2.5vw, 32px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {selectedOption.icon}
            </span>
          ) : (
            !selectedOption && (
              <Search
                style={{
                  color: isOpen ? "#BEBCC2" : "#687099",
                  width: "clamp(25px, 2.5vw, 32px)",
                  height: "clamp(25px, 2.5vw, 32px)",
                  flexShrink: 0,
                }}
              />
            )
          )}

          <input
            onClick={() => setIsOpen(!isOpen)}
            ref={searchInputRef}
            type="text"
            placeholder={selectedOption ? selectedOption.label : placeholder}
            value={isOpen ? searchTerm : selectedOption?.label || ""}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border-none outline-hidden bg-transparent transition-colors duration-150 font-semibold2"
            style={{
              color: selectedOption ? "#C9B8FF" : "#687099",
              boxShadow: "none",
              fontWeight: "400",
              fontSize: "clamp(16px, 1.5vw, 18px)",
              cursor: disabled ? "not-allowed" : "pointer",
            }}
          />

          {selectedOption ? (
            <Check
              strokeWidth={2.5}
              style={{
                color: "#C9B8FF",
                width: "clamp(25px, 2.5vw, 32px)",
                height: "clamp(25px, 2.5vw, 32px)",
                flexShrink: 0,
              }}
            />
          ) : (
            <ChevronRight
              onClick={() => setIsOpen(!isOpen)}
              strokeWidth={2.5}
              style={{
                color: isOpen ? "#BEBCC2" : "#687099",
                width: "clamp(25px, 2.5vw, 32px)",
                height: "clamp(25px, 2.5vw, 32px)",
                flexShrink: 0,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            />
          )}
        </div>
      </div>

      {/* Opciones desplegables - POSITION FIXED */}
      {isOpen && dropdownPosition.width > 0 && (
        <div
          className="overflow-y-auto"
          style={{
            width: `${dropdownPosition.width}px`,
            backgroundColor: "#29214F",
            borderRadius: "8px",
            overflow: "auto",
            scrollbarWidth: "none",
            border: "1px solid #565584",
            height: isDesktop
              ? filteredOptions.length < 2
                ? "50px"
                : filteredOptions.length === 2
                  ? "100px"
                  : "200px"
              : filteredOptions.length < 2
                ? "50px"
                : filteredOptions.length === 2
                  ? "100px"
                  : "270px",
            zIndex: 9999,
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5)",
            position: "absolute",
            top: isDesktop ? "clamp(110px, 6vw, 160px)" : "110px",
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isSelected = (option.value || option.label) === value;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleSelect(option)}
                  className="w-full px-4 py-3 flex justify-between items-center text-left transition-all duration-150"
                  style={{
                    borderBottom:
                      index < filteredOptions.length - 1
                        ? "1px solid #687099"
                        : "none",
                    backgroundColor: isSelected ? "#565584" : "#29214F",
                    color: isSelected ? "#C9B8FF" : "#687099",
                    cursor: "pointer",
                  }}
                >
                  <div className="flex justify-start gap-2">
                    <span>{option.icon && option.icon}</span>
                    <span>{option.label}</span>
                  </div>
                </button>
              );
            })
          ) : (
            <div
              className="px-4 py-3 text-gray-400 text-lg text-center"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "-webkit-fill-available",
              }}
            >
              No se encontraron opciones
            </div>
          )}
        </div>
      )}

      {/* Cerrar al hacer clic afuera */}
      {isOpen && (
        <div
          className="fixed inset-0"
          style={{ zIndex: 9998 }}
          onClick={() => setIsOpen(false)}
        />
      )}

      {error && (
        <div className="flex flex-row items-center gap-3 ">
          <InfoIcon className="w-5 h-5 text-red" />
          <p className=" text-red" style={{ fontSize: "15px" }}>
            {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default FilterableSelect;
